// Moteur cosmétique (code pur partagé) -- Calculs du formulateur cosmetique (code pur).
//
// Porte fidelement du V1 : conversion pourcentage -> grammes sur un poids de
// batch, sommes par phase, controle de la somme totale (~100 %) et cout.
// Les arrondis reproduisent ceux du V1 (qte a 0,01 g, sommes a 0,1 %).

import type { FormuleCosmetique, LigneFormule, PhaseId } from "./types";
import { PHASES_ORDRE } from "./types";

/** Tolerance V1 : au-dela de 100,5 % on signale un depassement. */
export const TOLERANCE_SOMME = 0.5;

/** Quantite en grammes d'une ligne pour un poids total donne (arrondi 0,01 g). */
export function quantiteGrammes(poidsTotal: number, pourcentage: number): number {
  const pct = Number.isFinite(pourcentage) ? pourcentage : 0;
  return Math.round((poidsTotal * pct) / 100 * 100) / 100;
}

/** Somme des pourcentages d'une phase (arrondi 0,1 %). */
export function sommePhase(lignes: LigneFormule[] | undefined): number {
  const s = (lignes ?? []).reduce(
    (acc, l) => acc + (Number.isFinite(l.pourcentage) ? l.pourcentage : 0),
    0,
  );
  return Math.round(s * 10) / 10;
}

/** Recupere les lignes d'une phase de la formule. */
function lignesPhase(formule: FormuleCosmetique, phase: PhaseId): LigneFormule[] {
  return (formule[phase] as LigneFormule[]) ?? [];
}

/** Somme totale des pourcentages de toutes les phases (arrondi 0,1 %). */
export function sommeTotale(formule: FormuleCosmetique): number {
  const s = PHASES_ORDRE.reduce(
    (acc, phase) =>
      acc +
      lignesPhase(formule, phase).reduce(
        (a, l) => a + (Number.isFinite(l.pourcentage) ? l.pourcentage : 0),
        0,
      ),
    0,
  );
  return Math.round(s * 10) / 10;
}

export interface ControleSomme {
  /** Somme totale des pourcentages (0,1 %). */
  total: number;
  /** true si total est dans [100 - tol ; 100 + tol]. */
  conforme: boolean;
  /** true si total depasse 100 % + tolerance. */
  depassement: boolean;
}

/** Controle de la somme : doit avoisiner 100 % (tolerance TOLERANCE_SOMME). */
export function controleSomme100(
  formule: FormuleCosmetique,
  tolerance: number = TOLERANCE_SOMME,
): ControleSomme {
  const total = sommeTotale(formule);
  return {
    total,
    conforme: Math.abs(total - 100) <= tolerance,
    depassement: total > 100 + tolerance,
  };
}

/** Cout total de la formule en euros, via prix_kg des lignes (arrondi 0,01 EUR). */
export function coutFormule(formule: FormuleCosmetique): number {
  const cout = PHASES_ORDRE.reduce((acc, phase) => {
    return (
      acc +
      lignesPhase(formule, phase).reduce((a, l) => {
        const prixG = l.prix_kg ? l.prix_kg / 1000 : 0;
        return a + quantiteGrammes(formule.poids_total, l.pourcentage) * prixG;
      }, 0)
    );
  }, 0);
  return Math.round(cout * 100) / 100;
}
